import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Flame, Plus, Trash2, Dumbbell, Clock, Check, X, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { CalorieHistory } from './training/CalorieHistory';

const ACTIVITIES = [
  { value: 'cardio', label: 'Cardio' },
  { value: 'hiit', label: 'HIIT' },
  { value: 'course', label: 'Course' },
  { value: 'natation', label: 'Natation' },
  { value: 'velo', label: 'Velo' },
  { value: 'vaa', label: "Va'a" },
  { value: 'marche', label: 'Marche' },
  { value: 'autre', label: 'Autre' },
];

export default function TrainingCaloriesPage() {
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddExtra, setShowAddExtra] = useState(false);
  const [extraForm, setExtraForm] = useState({ activity: 'cardio', duration_min: '30', calories: '' });
  const [editingTotal, setEditingTotal] = useState(false);
  const [editTotalValue, setEditTotalValue] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/training/today');
      setTraining(data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExercise = async (index, done) => {
    try {
      const { data } = await apiClient.put('/training/today/toggle', { exercise_index: index, done });
      setTraining(prev => {
        const exs = [...prev.exercises];
        exs[index] = { ...exs[index], done };
        return { ...prev, exercises: exs, total_calories: data.total_calories };
      });
    } catch { toast.error('Erreur'); }
  };

  const addExtra = async () => {
    if (!extraForm.duration_min || parseInt(extraForm.duration_min) <= 0) { toast.error('Renseigne la duree'); return; }
    try {
      const { data } = await apiClient.post('/training/today/extra', {
        activity: extraForm.activity,
        duration_min: parseInt(extraForm.duration_min),
        calories: extraForm.calories ? parseInt(extraForm.calories) : null,
      });
      toast.success(`+${data.calories} kcal ajoutees`);
      setExtraForm({ activity: 'cardio', duration_min: '30', calories: '' });
      setShowAddExtra(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const removeExtra = async (index) => {
    try {
      await apiClient.delete(`/training/today/extra/${index}`);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleAdjustTotal = async () => {
    const val = parseInt(editTotalValue);
    if (isNaN(val) || val < 0) { toast.error('Valeur invalide'); return; }
    try {
      await apiClient.put('/training/today/adjust', { total_calories: val });
      setTraining(prev => ({ ...prev, total_calories: val, manual_override: true }));
      setEditingTotal(false);
      toast.success('Calories ajustees !');
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  if (!training) return <p className="text-center text-zinc-500 py-8">Pas de donnees</p>;

  const exercisesDone = training.exercises?.filter(e => e.done).length || 0;
  const exercisesTotal = training.exercises?.length || 0;

  return (
    <div className="p-4 space-y-4" data-testid="training-calories">
      {/* Total calories */}
      <div className="bg-gradient-to-r from-[#FF6B35]/20 to-[#FFB800]/20 border border-[#FF6B35]/30 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-[#FF6B35] uppercase tracking-wider font-bold">{training.session_name}</p>
            {training.session_time && <p className="text-[10px] text-zinc-500">{training.session_time} · {training.session_duration || 60} min</p>}
            {!editingTotal ? (
              <div className="flex items-center gap-2 mt-1">
                <p className="font-mono text-4xl font-bold text-white" data-testid="total-calories">{training.total_calories}</p>
                <button onClick={() => { setEditingTotal(true); setEditTotalValue(String(training.total_calories)); }}
                  className="text-zinc-500 hover:text-[#FF6B35] p-1" data-testid="edit-total-btn">
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-1">
                <input type="number" value={editTotalValue} onChange={e => setEditTotalValue(e.target.value)}
                  className="w-24 bg-[#0F0F0F] border border-[#FF6B35]/50 rounded-lg text-3xl text-white font-mono px-2 py-1 text-right"
                  autoFocus onKeyDown={e => { if (e.key === 'Enter') handleAdjustTotal(); if (e.key === 'Escape') setEditingTotal(false); }}
                  data-testid="edit-total-input" />
                <button onClick={handleAdjustTotal} className="p-1.5 bg-green-600 rounded-lg text-white hover:bg-green-700" data-testid="save-total-btn">
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => setEditingTotal(false)} className="p-1.5 bg-zinc-700 rounded-lg text-zinc-300 hover:bg-zinc-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <p className="text-xs text-zinc-400">kcal brulees {training.manual_override && <span className="text-[#FFB800]">(ajuste manuellement)</span>}</p>
          </div>
          {!training.is_rest_day && (
            <div className="text-right">
              <p className="font-mono text-2xl font-bold text-[#FF6B35]">{exercisesDone}/{exercisesTotal}</p>
              <p className="text-[10px] text-zinc-500">exercices</p>
            </div>
          )}
        </div>
        {training.auto_generated && (
          <p className="text-[9px] text-zinc-600 mt-2">Calcule automatiquement selon ton profil ({training.weight_used}kg, {training.gender === 'female' ? 'F' : 'H'}, {training.age}ans) — decoche ce que tu n'as pas fait</p>
        )}
      </div>

      {/* Exercises from program */}
      {training.exercises?.length > 0 && (
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#2A2A2E]">
            <h3 className="text-xs font-bold text-white uppercase tracking-wider">Exercices du programme</h3>
          </div>
          <div className="divide-y divide-[#2A2A2E]/50">
            {training.exercises.map((ex, i) => (
              <button key={i} onClick={() => toggleExercise(i, !ex.done)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${ex.done ? '' : 'opacity-40'}`}
                data-testid={`exercise-${i}`}>
                <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${ex.done ? 'bg-[#2e7d32]' : 'bg-[#2A2A2E]'}`}>
                  {ex.done ? <Check className="w-4 h-4 text-white" /> : <X className="w-4 h-4 text-zinc-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${ex.done ? 'text-white' : 'text-zinc-500 line-through'}`}>{ex.name}</p>
                  <p className="text-[10px] text-zinc-500">{ex.sets}x{ex.reps} · {ex.rest}</p>
                </div>
                <span className={`font-mono text-xs font-bold ${ex.done ? 'text-[#FF6B35]' : 'text-zinc-600'}`}>
                  {ex.done ? `-${ex.calories}` : '0'} kcal
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Extra activities */}
      {training.extra_activities?.length > 0 && (
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4">
          <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-2">Activites supplementaires</h3>
          <div className="space-y-2">
            {training.extra_activities.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 bg-[#0F0F0F] rounded-lg">
                <Flame className="w-4 h-4 text-[#FFB800] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white capitalize">{a.activity}</p>
                  <p className="text-[10px] text-zinc-500">{a.duration_min} min</p>
                </div>
                <span className="font-mono text-xs font-bold text-[#FFB800]">-{a.calories} kcal</span>
                <button onClick={() => removeExtra(i)} className="text-red-400 hover:text-red-300 p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add extra activity */}
      {!showAddExtra ? (
        <Button onClick={() => setShowAddExtra(true)} variant="outline" className="w-full border-[#2A2A2E] text-zinc-400" data-testid="add-extra-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter une activite (cardio, sport, etc.)
        </Button>
      ) : (
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-white">Activite supplementaire</h3>
            <button onClick={() => setShowAddExtra(false)} className="text-zinc-500"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ACTIVITIES.map(a => (
              <button key={a.value} onClick={() => setExtraForm({...extraForm, activity: a.value})}
                className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${extraForm.activity === a.value ? 'bg-[#FFB800] text-black' : 'bg-[#0F0F0F] text-zinc-400 border border-[#2A2A2E]'}`}>
                {a.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-[10px]"><Clock className="w-3 h-3 inline" /> Duree (min)</Label>
              <Input type="number" min="1" value={extraForm.duration_min} onChange={e => setExtraForm({...extraForm, duration_min: e.target.value})}
                className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono h-9" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px]"><Flame className="w-3 h-3 inline" /> kcal (optionnel)</Label>
              <Input type="number" min="0" value={extraForm.calories} onChange={e => setExtraForm({...extraForm, calories: e.target.value})}
                placeholder="auto" className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono h-9" />
            </div>
          </div>
          <Button onClick={addExtra} className="w-full bg-[#FFB800] text-black font-bold hover:bg-[#FFD000]">Ajouter</Button>
        </div>
      )}

      {/* Rest day message */}
      {training.is_rest_day && training.extra_activities?.length === 0 && (
        <div className="text-center py-4">
          <p className="text-zinc-500 text-sm">Jour de repos</p>
          <p className="text-zinc-600 text-xs">Ajoute une activite si tu as bouge quand meme !</p>
        </div>
      )}

      {/* 30-day Calorie History */}
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4">
        <CalorieHistory />
      </div>
    </div>
  );
}
